import { AuditStamp } from '../../../shared/audit/audit.models';
﻿import { Products } from '../../../order/models/product.model';
import { AccountingCatalogDescriptionComponent, AccountingCatalog } from '../../shared/accounting-catalog-description.component';
import { AccountingEditorPartyState } from '../../shared/accounting-editor-party-state';
import { AccountingDraftEditRequest } from '../../shared/accounting-draft-edit.model';
import { RatedEditorLine, rateSnapshot, recalculateRatedLine, validRatedLine, restoreTaxCodes } from '../../shared/accounting-editor-line';
import { currencyFractionDigits, roundCurrency } from '../../../shared/utils/commercial-money';
import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { FileSelectEvent, FileUploadModule } from 'primeng/fileupload';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import * as XLSX from 'xlsx';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { PageHeroComponent } from '../../../shared/page-layout/page-hero.component';
import { PageFormSectionComponent } from '../../../shared/page-layout/page-form-section.component';
import { AccountsService } from '../../accounts.service';
import { AccountingTaxCode } from '../../accounting-tax-code.model';
import { recalculateLineTax } from '../../shared/accounting-line-tax';
import { accountingNumberWidth } from '../../shared/accounting-line-width';
import { ManualEntryReasonCode } from '../../manual-entry-control.model';
import { AccountingAttachmentsPanelComponent } from '../../shared/accounting-attachments-panel.component';
import { AccountReceivables } from '../receivables.model';
import { ReceivablesLineType } from '../receivable-line-type.model';
import { Country } from '../../../models/common/country.model';
import { SessionStorageService } from '../../../services/session-storage.service';
import { PartyService } from '../../../party/party.service';
import { Party } from '../../../party/party.model';
import { CrmClient, CrmClientCommunication } from '../../../crm/crm.model';
import { CRM_RECEIVABLE_ROLES, CrmPartyContextService } from '../../../shared/crm-client-contact-picker/crm-party-context.service';
import { CommercialPartyPickerComponent } from '../../../shared/commercial-party-picker/commercial-party-picker.component';
import { CommercialPartyReference } from '../../../shared/commercial-party-picker/commercial-party.model';
import { CrmQuickCreateResult } from '../../../shared/crm-quick-create/crm-quick-create-dialog.component';

interface ManualReceivableLine extends RatedEditorLine {
    savedTaxCode?: string | null;
    receivableLineId?: number;
    description: string;
    itemDescriptionLocal: string;
    invoiceComment: string;
    reference: string;
    quantity: number;
    amountExclTax: number;
    taxAmount: number;
    taxCode: string;
    type: ReceivablesLineType;
}

interface ManualReadinessStatusOption {
    label: string;
    value: string;
}

@Component({
    selector: 'app-manual-receivable-entry',
    standalone: true,
    imports: [CommonModule, FormsModule, ButtonModule, DatePickerModule, FileUploadModule, IftaLabelModule, InputGroupModule, InputGroupAddonModule, InputNumberModule, InputTextModule, SelectModule, TextareaModule, ToastModule, PageShellComponent, PageHeroComponent, PageFormSectionComponent, AccountingAttachmentsPanelComponent, CommercialPartyPickerComponent, AccountingCatalogDescriptionComponent],
    providers: [MessageService, AccountingEditorPartyState],
    templateUrl: './manual-receivable-entry.component.html',
    styleUrls: ['./manual-receivable-entry.component.css', '../../shared/accounting-line-layout.css']
})
export class ManualReceivableEntryComponent implements OnInit {
    readonly numberInputWidth = accountingNumberWidth;
    commercialPartyReference: CommercialPartyReference = { source: 'CRM' };
    readonly baseStatusOptions: ManualReadinessStatusOption[] = [
        { label: 'NEW', value: 'NEW' },
        { label: 'READY', value: 'READY' },
        { label: 'ON_HOLD', value: 'ON_HOLD' },
        { label: 'DISPUTED', value: 'DISPUTED' },
        { label: 'APPROVAL_REQUIRED', value: 'APPROVAL_REQUIRED' }
    ];
    parties: Party[] = [];
    crmClients: CrmClient[] = [];
    crmContacts: CrmClientCommunication[] = [];
    reasonCodeOptions: ManualEntryReasonCode[] = [];
    taxCodeOptions: AccountingTaxCode[] = [];
    party?: Party;
    selectedCrmClient: CrmClient | null = null;
    selectedCrmContact: CrmClientCommunication | null = null;
    documentReference = '';
    externalReference = '';
    readonly lineTypeOptions = Object.values(ReceivablesLineType);
    reasonCode = 'MANUAL_RECEIVABLE';
    reasonText = '';
    defaultTaxCode = 'VAT';
    manualAttachmentRequired = false;
    status = 'APPROVAL_REQUIRED';
    documentDate = new Date();
    country?: Country;
    receivableId?: number;
	private rowVersion?: number;
	saving = false;
    loading = false;
    loadFailed = false;
    editable = true;
    sourceType = 'MANUAL';
    catalog: Products[] = [];
    lines: ManualReceivableLine[] = [this.blankLine()];

    constructor(private accountsService: AccountsService, private sessionStorage: SessionStorageService, private partyService: PartyService, private crmPartyContext: CrmPartyContextService, private messageService: MessageService, private route: ActivatedRoute, private router: Router, public partyState: AccountingEditorPartyState, private catalogs: AccountingCatalog) {}

    ngOnInit(): void {
        const companyId = this.sessionStorage.getCompany()?.id;
        if (companyId) {
            this.catalogs.load(companyId).subscribe({ next: rows => this.catalog = rows.filter(p => !p.deletedAt && p.owningCompanyId === companyId), error: () => this.catalog = [] });
            this.partyService.getParties(companyId, [], '', 0, 1000).subscribe(page => this.parties = page.content);
            this.crmPartyContext.loadClients(companyId, CRM_RECEIVABLE_ROLES).subscribe(clients => this.crmClients = clients);
            this.loadMasterData(companyId);
        }
        const id = Number(this.route.snapshot.paramMap.get('id'));
        if (id) { this.loading = true; this.loadReceivable(id); }
    }

    get isEditMode(): boolean { return !!this.receivableId; }
    get totalExclTax(): number { return this.lines.reduce((sum, line) => sum + Number(line.amountExclTax || 0), 0); }
    get totalTax(): number { return this.lines.reduce((sum, line) => sum + Number(line.taxAmount || 0), 0); }
    get totalAmount(): number { return this.totalExclTax + this.totalTax; }
    get canSubmit(): boolean {
        const enteredLines = this.lines;
        return (!this.receivableId || this.rowVersion !== undefined) && !this.loading && !this.loadFailed && this.editable && !this.partyState.busy && !this.partyState.error
            && !!this.partyState.party?.id && !!this.reasonCode && !!this.reasonText.trim() && !!this.status
            && enteredLines.length > 0
            && enteredLines.every(line => !!line.description.trim() && !!line.type && validRatedLine(line))
            && (!this.requiresBilingualDescriptions
                || enteredLines.every(line => this.isArabicOnly(line.itemDescriptionLocal)));
    }
    get requiresBilingualDescriptions(): boolean {
        const status = this.status?.trim().toUpperCase();
        return !!this.sessionStorage.getCompany()?.requireBilingualSingleInvoice
            && ['READY', 'APPROVAL_REQUIRED', 'APPROVED'].includes(status);
    }
    get statusOptions(): ManualReadinessStatusOption[] {
        return this.status && !this.baseStatusOptions.some(option => option.value === this.status)
            ? [{ label: this.status, value: this.status }, ...this.baseStatusOptions]
            : this.baseStatusOptions;
    }

    addLine(): void { if (this.sourceType !== 'MANUAL') return; this.lines = [...this.lines, this.blankLine()]; }
    removeLine(index: number): void { if (this.sourceType !== 'MANUAL') return; this.lines = this.lines.length > 1 ? this.lines.filter((_, i) => i !== index) : this.lines; }
    lineTotal(line: ManualReceivableLine): number { return Number(line.amountExclTax || 0) + Number(line.taxAmount || 0); }
    money(value?: number): string { const digits = currencyFractionDigits(this.currencyCode); return `${this.currencyCode} ${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits })}`; }
    lineTabIndex(rowIndex: number, columnIndex: number): number { return 100 + rowIndex * 20 + columnIndex; }
    applyDefaultTaxCode(): void {
        this.lines = this.lines.map(line => line.taxCode
            ? line
            : recalculateLineTax({ ...line, taxCode: this.defaultTaxCode }, this.taxCodeOptions, this.currencyCode));
    }
    recalculateLineTax(line: ManualReceivableLine): void {
        line.recalculateTax = true;
        Object.assign(line, recalculateLineTax(line, this.taxCodeOptions, this.currencyCode));
    }

    onProductSelected(line: ManualReceivableLine, product: Products): void {
        line.productId = product.productID; line.productCode = product.productCode; line.incomeAccountId = product.incomeAccountId;
        line.description = product.description || product.name;
        line.itemDescriptionLocal = product.descriptionLocal || "";
        line.unitRate = Number(product.sellingRate ?? product.standardRate ?? 0);
        this.recalculateLine(line);
    }
    clearCatalog(line: ManualReceivableLine): void { line.productId = undefined; line.productCode = undefined; line.incomeAccountId = undefined; this.recalculateLine(line); }
    recalculateLine(line: ManualReceivableLine): void { recalculateRatedLine(line, this.taxCodeOptions, this.currencyCode); }
    downloadExcelTemplate(): void {
        const rows = this.isEditMode ? this.lines.map(line => ({
            lineId: line.receivableLineId, description: line.description, productCode: line.productCode || '',
            itemDescriptionLocal: line.itemDescriptionLocal, invoiceComment: line.invoiceComment,
            reference: line.reference, type: line.type, quantity: line.quantity, rate: line.unitRate, amountExclTax: line.amountExclTax, taxCode: line.taxCode
        })) : [{ description: 'Service charge', productCode: '', itemDescriptionLocal: "", invoiceComment: "", reference: '', type: ReceivablesLineType.MISCELLANEOUS, quantity: 1, rate: 100, taxCode: this.defaultTaxCode }];
        const worksheet = XLSX.utils.json_to_sheet(rows);
        const buffer = XLSX.write({ Sheets: { lines: worksheet }, SheetNames: ['lines'] }, { bookType: 'xlsx', type: 'array' });
        this.downloadWorkbook(buffer, 'receivable-lines-template.xlsx');
    }
    importExcel(event: FileSelectEvent): void {
        const file = event.files?.[0]; if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            try {
                const workbook = XLSX.read(reader.result, { type: 'array' });
                this.importRows(XLSX.utils.sheet_to_json<Record<string, unknown>>(workbook.Sheets[workbook.SheetNames[0]]));
            } catch (error) { this.messageService.add({ severity: 'error', summary: 'Import failed', detail: error instanceof Error ? error.message : 'Invalid workbook.' }); }
        };
        reader.readAsArrayBuffer(file);
    }
    importRows(rows: Record<string, unknown>[]): void {
        const ids = rows.map(row => Number(this.pick(row, ['lineId', 'receivableLineId'])) || undefined);
        if (this.sourceType !== 'MANUAL' && (ids.length !== this.lines.length || new Set(ids).size !== this.lines.length || ids.some(id => !this.lines.some(line => line.receivableLineId === id))))
            throw new Error('Generated imports must retain every existing line ID and cannot add or remove lines.');
        const imported = rows.map((row, index) => {
            const saved = this.lines.find(line => line.receivableLineId === ids[index]);
            const code = String(this.pick(row, ['productCode', 'itemNumber']) ?? '');
            const product = code ? this.catalog.find(p => p.productCode?.toLowerCase() === code.toLowerCase()) : undefined;
            if (code && !product) throw new Error(`Catalog product ${code} is not available for this company.`);
            const quantity = Number(this.pick(row, ['quantity', 'qty']) ?? 1);
            const rate = this.pick(row, ['rate', 'unitRate', 'unitPrice']) ?? (this.pick(row, ['amountExclTax', 'amount', 'netAmount', 'chargeAmount']) === undefined && product ? product.sellingRate ?? product.standardRate : undefined);
            const amount = roundCurrency(Number(this.pick(row, ['amountExclTax', 'amount', 'netAmount', 'chargeAmount']) ?? 0), this.currencyCode);
            const line: ManualReceivableLine = { ...this.blankLine(), receivableLineId: ids[index],
                productId: product?.productID ?? saved?.productId, productCode: product?.productCode ?? saved?.productCode, incomeAccountId: product?.incomeAccountId ?? saved?.incomeAccountId,
                description: String(this.pick(row, ['description', 'itemDescription']) ?? product?.description ?? product?.name ?? ''),
                itemDescriptionLocal: String(this.pick(row, ["itemDescriptionLocal", "arabicDescription"]) ?? product?.descriptionLocal ?? ""), invoiceComment: String(this.pick(row, ["invoiceComment", "comment"]) ?? ""),
                reference: this.sourceType !== 'MANUAL' ? saved?.reference || '' : String(this.pick(row, ['reference', 'sourceReference']) ?? ''),
                type: String(this.pick(row, ['type', 'lineType']) ?? ReceivablesLineType.MISCELLANEOUS) as ReceivablesLineType,
                quantity, ...rateSnapshot(quantity, amount, rate === undefined ? undefined : Number(rate)), amountExclTax: amount,
                taxCode: String(this.pick(row, ['taxCode']) ?? this.defaultTaxCode), recalculateTax: true };
            line.rateEntered = true;
            if (rate !== undefined) this.recalculateLine(line); else this.recalculateLineTax(line);
            return line;
        });
        this.lines = imported.length ? imported : [this.blankLine()];
    }

    save(): void {
		if (this.saving || !this.canSubmit) return;
        const company = this.sessionStorage.getCompany();
        const country = this.resolveCountry();
        const payload = {
            receivableId: this.receivableId,
			rowVersion: this.rowVersion,
            receivableReferenceNumber: this.documentReference || undefined,
            externalReferenceNumber: this.externalReference || undefined,
            country,
            totalAmount: this.totalAmount,
            amountExclTax: this.totalExclTax,
            taxAmount: this.totalTax,
            invoicingDate: this.documentDate,
            status: this.status,
            sourceType: 'MANUAL',
            sourceReference: this.externalReference || undefined,
            manualReasonCode: this.reasonCode,
            manualReasonText: this.reasonText,
            manualAttachmentRequired: this.manualAttachmentRequired,
            createdFromModule: 'PORTAL_MANUAL_RECEIVABLE',
            crmClientId: this.commercialPartyReference.crmClientId ?? undefined,
            crmAccountId: this.commercialPartyReference.crmAccountId ?? undefined,
            crmClientContactId: this.commercialPartyReference.crmContactId ?? undefined,
            mainParty: this.partyState.party,
            subParty: this.partyState.contact || this.partyState.party,
            owningCompany: company,
            createdAt: new Date(),
            receivableTxnType: 'ORDER',
            accountReceivablesLines: this.lines.filter(line => this.lineTotal(line) > 0).map(line => ({
                receivableLineId: line.receivableLineId,
                receivableLineRefNumber: line.reference || this.externalReference || undefined,
                lineDescription: line.description.trim(),
                itemDescriptionLocal: line.itemDescriptionLocal.trim() || undefined,
                invoiceComment: line.invoiceComment.trim() || undefined,
                receivablesLineType: line.type,
                amount: this.lineTotal(line),
                amountExclTax: line.amountExclTax,
                taxAmount: line.taxAmount,
                quantity: line.quantity, incomeAccountId: line.incomeAccountId, unitRate: line.unitRate, taxCode: line.taxCode, productId: line.productId, productCodeSnapshot: line.productCode,
                headerReferenceId: 0,
                detailReferenceId: 0
            }))
        } as unknown as AccountReceivables;
		const createdNow = !this.receivableId;
        const request = this.receivableId ? this.accountsService.editAccountReceivable(this.receivableId, this.editRequest()) : this.accountsService.createAccountReceivables(payload);
		this.saving = true;
        request.subscribe({
            next: saved => {
				this.receivableId = saved.receivableId;
				this.rowVersion = saved.rowVersion;
				if (saved.receivableId) this.loadReceivable(saved.receivableId);
                this.messageService.add({ severity: 'success', summary: 'Saved', detail: `Manual receivable ${saved.receivableReferenceNumber || saved.receivableId} saved.` });
				this.saving = false;
                if (createdNow && saved.receivableId) this.router.navigate(['/accounts/enterprise/receivables/to-be-invoiced/manual', saved.receivableId]);
            },
			error: err => {
				this.saving = false;
				const detail = err?.status === 409
					? 'Draft changed elsewhere. The latest version was reloaded.'
					: err?.error?.message || 'Could not save manual receivable.';
				if (err?.status === 409 && this.receivableId) this.loadReceivable(this.receivableId);
				this.messageService.add({ severity: 'error', summary: 'Save failed', detail });
			}
        });
    }

    back(): void { this.router.navigate(['/accounts/enterprise/receivables/to-be-invoiced']); }
    get companyId(): number | undefined { return this.sessionStorage.getCompany()?.id; }
    get actorId(): number | undefined { return this.sessionStorage.getCustomer()?.id; }

    onCrmQuickCreated(result: CrmQuickCreateResult): void { if (result.mode === 'client') this.crmClients = [...this.crmClients, result.record]; }
    onCommercialPartyChange(reference: CommercialPartyReference): void {
        this.partyState.change(reference, this.companyId!).subscribe(() => this.syncParty());
        this.commercialPartyReference = this.partyState.reference;
    }
    private syncParty(): void { this.party = this.partyState.party || undefined; this.selectedCrmClient = this.partyState.client; }
    private editRequest(): AccountingDraftEditRequest {
        return { rowVersion: this.rowVersion, owningCompanyId: this.companyId,
            mainParty: this.partyState.party, subParty: this.partyState.contact || this.partyState.party,
            crmClientId: this.commercialPartyReference.crmClientId, crmAccountId: this.commercialPartyReference.crmAccountId,
            crmClientContactId: this.commercialPartyReference.crmContactId, documentDate: this.documentDate,
            externalReference: this.externalReference, status: this.status, manualReasonCode: this.reasonCode, manualReasonText: this.reasonText,
            lines: this.lines.map(line => ({ lineId: line.receivableLineId, description: line.description.trim(),
                descriptionLocal: line.itemDescriptionLocal.trim(), invoiceComment: line.invoiceComment.trim(),
                reference: line.reference, lineType: line.type, productId: line.productId, quantity: line.quantity,
                unitRate: line.rateEntered ? line.unitRate : null, taxCode: line.rateEntered || line.recalculateTax ? line.taxCode : line.savedTaxCode,
                recalculateTax: line.recalculateTax })) };
    }

    private loadMasterData(companyId: number): void {
        this.accountsService.getManualEntryReasonCodes(companyId, 'MANUAL_RECEIVABLE_LINE').subscribe(codes => {
            this.reasonCodeOptions = codes.length ? codes : [{ documentType: 'MANUAL_RECEIVABLE_LINE', reasonCode: this.reasonCode, description: this.reasonCode }];
        });
        this.accountsService.getAccountingTaxCodes(companyId).subscribe(codes => {
            this.taxCodeOptions = codes.length ? codes : [{ taxCode: 'VAT', description: 'VAT', taxRate: 15 }];
            this.defaultTaxCode = this.taxCodeOptions[0]?.taxCode || this.defaultTaxCode;
            this.taxCodeOptions = restoreTaxCodes(this.lines, this.taxCodeOptions);
            this.applyDefaultTaxCode();
        });
    }

    private loadReceivable(id: number): void {
        this.loading = true;
        this.accountsService.getAccountReceivableById(id).subscribe({
            next: document => { this.hydrate(document); this.loading = false; this.loadFailed = false; },
            error: () => { this.loading = false; this.loadFailed = true; this.messageService.add({ severity: 'error', summary: 'Load failed', detail: 'Could not load the record.' }); }
        });
    }
    auditStamp?: AuditStamp;

    private hydrate(document: AccountReceivables): void {
        this.auditStamp = document;
        this.receivableId = document.receivableId; this.rowVersion = document.rowVersion;
        this.sourceType = document.sourceType || 'SYSTEM';
        this.editable = document.owningCompany?.id === this.companyId && !document.invoiceId && !document.invoiceNumber && !['INVOICED','CANCELLED','REVERSED'].includes(document.status || '');
        this.country = document.country || this.resolveCountry();
        this.partyState.restore(document, this.companyId!); this.commercialPartyReference = this.partyState.reference; this.syncParty();
        this.documentReference = document.receivableReferenceNumber || ''; this.externalReference = document.externalReferenceNumber || '';
        this.reasonCode = document.manualReasonCode || this.reasonCode; this.reasonText = document.manualReasonText || '';
        this.status = document.status || this.status; this.documentDate = document.invoicingDate ? new Date(document.invoicingDate!) : new Date();
        this.lines = (document.accountReceivablesLines || []).map(line => {
            const quantity = line.quantity ?? 1; const amount = line.amountExclTax ?? ((line.amount || 0) - (line.taxAmount || 0));
            return { receivableLineId: line.receivableLineId, description: line.lineDescription || '',
                itemDescriptionLocal: line.itemDescriptionLocal || "", invoiceComment: line.invoiceComment || "",
                reference: line.receivableLineRefNumber || '', type: line.receivablesLineType || ReceivablesLineType.MISCELLANEOUS,
                productId: line.productId, productCode: line.productCodeSnapshot, quantity, ...rateSnapshot(quantity, amount, line.unitRate),
                amountExclTax: amount, taxAmount: line.taxAmount || 0, savedTaxCode: line.taxCode ?? null,
                taxCode: line.taxCode || ((line.taxAmount || 0) > 0 ? 'VAT' : 'EXEMPT') };
        });
        if (!this.lines.length) this.lines = [this.blankLine()];
        this.taxCodeOptions = restoreTaxCodes(this.lines, this.taxCodeOptions);
        for (const line of this.lines) if (!this.lineTypeOptions.includes(line.type)) this.lineTypeOptions.push(line.type);
    }

    private blankLine(): ManualReceivableLine { return { unitRate: null, rateEntered: true, recalculateTax: false, description: '', itemDescriptionLocal: '', invoiceComment: '', reference: this.externalReference, type: ReceivablesLineType.MISCELLANEOUS, quantity: 1, amountExclTax: 0, taxAmount: 0, taxCode: this.defaultTaxCode || 'VAT' }; }
    private isArabicOnly(value?: string): boolean {
        const text = value?.trim() || '';
        return /[\u0600-\u06FF]/.test(text) && !/[A-Za-z]/.test(text);
    }
    private pick(row: Record<string, unknown>, keys: string[]): unknown { const normalized = Object.entries(row).reduce<Record<string, unknown>>((map, [key, value]) => { map[key.replace(/\s|_/g, '').toLowerCase()] = value; return map; }, {}); return keys.map(key => normalized[key.replace(/\s|_/g, '').toLowerCase()]).find(value => value !== undefined && value !== null && value !== ''); }
    private downloadWorkbook(buffer: ArrayBuffer, fileName: string): void { const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }); const url = window.URL.createObjectURL(blob); const anchor = document.createElement('a'); anchor.href = url; anchor.download = fileName; anchor.click(); window.URL.revokeObjectURL(url); }
    private get currencyCode(): string { return this.resolveCountry()?.currencyCode || 'SAR'; }
    private resolveCountry(): Country | undefined {
        return this.country || this.sessionStorage.getCountryList()?.find(c => c.id === this.sessionStorage.getCompany()?.countryId);
    }
}
